import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import type { AttestationType } from '@shared/types/Attestation';
import { ToastService } from '@presentation/components/Toast';
import { FormHelper } from '@presentation/components/FormHelper';
import { openFormModal } from '@presentation/shell/openFormModal';
import { enhanceSearchableSelects } from '@presentation/components/SearchableSelect';
import { buildAttestationHTML } from '@presentation/print/attestationTemplate';
import { previewDocument } from '@presentation/print/PdfService';
import { listConges } from '@application/rh/CongesDirectoryService';
import { loadStcConfig } from '@application/rh/StcConfig';
import {
  congePourAttestation,
  messageAttestationVide,
  salariesEligiblesAttestation,
} from '@domain/rh/AttestationEligibility';
import { COMPANY_LEGAL_WARNING, isCompanyLegalComplete } from '@domain/legal/CompanyLegalInfo';
import { z } from 'zod';

const MatriculeSchema = z.object({
  matricule: z.string().min(1, 'Salarié requis'),
});

export const attestationsController: ViewController = async (section, ctx) => {
  const tenant = ctx.tenantContext.tryGetTenant();
  const tenantId = tenant?.id ?? 'default';
  const [salaries, bulletins, conges, stc] = await Promise.all([
    ctx.salaries.list(),
    ctx.bulletins.list(),
    listConges(tenantId),
    loadStcConfig(tenantId),
  ]);

  const latestBulletin = (salarieId: string): Bulletin | undefined =>
    bulletins
      .filter((b) => b.salarie_id === salarieId)
      .sort((a, b) => b.periode.localeCompare(a.periode))[0];

  const generer = (type: AttestationType, sal: Salarie): void => {
    if (!isCompanyLegalComplete(tenant)) {
      ToastService.warning(COMPANY_LEGAL_WARNING);
      return;
    }
    const conge = type === 'conges' ? congePourAttestation(conges, sal.id) : undefined;
    if (type === 'conges' && !conge) {
      ToastService.warning('Ce salarié n’a pas de congé approuvé ou en cours');
      return;
    }
    if ((type === 'salaire' || type === 'cnss') && !latestBulletin(sal.id)) {
      ToastService.warning('Calculez d’abord la paie pour ce salarié');
      return;
    }
    const doc = buildAttestationHTML(type, sal, tenant, latestBulletin(sal.id), conge, {
      conges: conges.filter((c) => c.salarie_id === sal.id),
      stc,
    });
    previewDocument(doc.html, doc.filename, doc.title);
  };

  const w = window as unknown as Record<string, unknown>;
  w['genererAttestation'] = (type: AttestationType) => {
    if (salaries.length === 0) {
      ToastService.error("Ajoutez d'abord des salariés");
      return;
    }
    const eligibles = salariesEligiblesAttestation(type, salaries, bulletins, conges);
    if (eligibles.length === 0) {
      ToastService.warning(messageAttestationVide(type));
      return;
    }
    openFormModal(section, { title: 'Choisir le salarié', size: 'sm' }, (host, close) => {
      host.innerHTML = `
        <form id="attestation-salarie-form">
          ${FormHelper.select(
            'matricule',
            'Salarié',
            eligibles.map((s) => ({
              value: s.matricule,
              label: `${s.prenom} ${s.nom} (${s.matricule})${s.poste ? ` — ${s.poste}` : ''}`,
            })),
            { required: true, placeholder: 'Rechercher un salarié…' },
          )}
          <p style="margin:0 0 12px;font-size:12px;color:var(--gris);">Saisissez un nom, un matricule ou une fonction pour filtrer la liste.</p>
          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
            <button type="button" class="btn btn-ghost" data-cancel>Annuler</button>
            <button type="submit" class="btn btn-primary">Générer</button>
          </div>
        </form>`;
      enhanceSearchableSelects(host);
      host.querySelector('[data-cancel]')?.addEventListener('click', close);
      FormHelper.bindSubmit(
        host.querySelector<HTMLFormElement>('#attestation-salarie-form')!,
        MatriculeSchema,
        (data) => {
          const sal = eligibles.find((s) => s.matricule === data.matricule);
          if (!sal) throw new Error('Salarié introuvable');
          close();
          generer(type, sal);
        },
      );
    });
  };
};
